import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarCheck,
  ExternalLink,
  Loader2,
  Phone,
  RefreshCw,
  Search,
  UserRound,
} from "lucide-react";
import { adminService } from "../../services/adminService";

const ConsultationBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [timing, setTiming] = useState("all");

  const loadBookings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await adminService.getConsultationBookings();
      const items = Array.isArray(payload) ? payload : [];
      setBookings(items);
      setSelectedId((current) =>
        current && items.some((item) => item.booking_id === current)
          ? current
          : items[0]?.booking_id || null
      );
    } catch (err) {
      console.error(err);
      setError("Failed to load consultation bookings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const statusOptions = useMemo(
    () => [...new Set(bookings.map((item) => item.status).filter(Boolean))],
    [bookings]
  );

  const filteredBookings = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const now = Date.now();
    return bookings.filter((item) => {
      const matchesQuery =
        !normalizedQuery ||
        [item.name, item.email, item.phone, item.booking_id].some((value) =>
          String(value || "").toLowerCase().includes(normalizedQuery)
        );
      const matchesStatus = status === "all" || item.status === status;
      const slotTime = item.slot_start ? Date.parse(item.slot_start) : 0;
      const matchesTiming =
        timing === "all" ||
        (timing === "upcoming" ? slotTime >= now : slotTime > 0 && slotTime < now);
      return matchesQuery && matchesStatus && matchesTiming;
    });
  }, [bookings, query, status, timing]);

  const selected =
    bookings.find((item) => item.booking_id === selectedId) || null;
  const now = Date.now();
  const confirmed = bookings.filter((item) => item.status === "confirmed").length;
  const upcoming = bookings.filter(
    (item) => item.slot_start && Date.parse(item.slot_start) >= now
  ).length;
  const needsAction = bookings.filter((item) =>
    ["paid_needs_reschedule", "payment_failed"].includes(item.status)
  ).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-display">Consultation Calls</h1>
          <p className="text-sm text-muted">
            Track people who chose a paid call before deciding whether to subscribe.
          </p>
        </div>
        <button
          type="button"
          onClick={loadBookings}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-title shadow-sm hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryCard label="Total bookings" value={bookings.length} />
        <SummaryCard label="Upcoming calls" value={upcoming} tone="teal" />
        <SummaryCard
          label="Needs attention"
          value={needsAction}
          hint={`${confirmed} confirmed`}
          tone={needsAction ? "amber" : "default"}
        />
      </div>

      <section className="rounded-lg bg-white p-4 shadow stakeholder-summary-card">
        <div className="grid gap-3 md:grid-cols-[minmax(240px,1fr)_180px_160px]">
          <label className="relative">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={17} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, email, phone or booking ID"
              className="w-full rounded-md border border-gray-200 py-2 pl-9 pr-3 text-sm"
            />
          </label>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-title"
          >
            <option value="all">All statuses</option>
            {statusOptions.map((option) => (
              <option key={option} value={option}>{formatLabel(option)}</option>
            ))}
          </select>
          <select
            value={timing}
            onChange={(event) => setTiming(event.target.value)}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-title"
          >
            <option value="all">Any time</option>
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
          </select>
        </div>
      </section>

      {error && (
        <div className="rounded-md border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.7fr)_minmax(300px,0.8fr)]">
        <section className="overflow-hidden rounded-lg bg-white shadow stakeholder-summary-card">
          <div className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-title">
            {filteredBookings.length} {filteredBookings.length === 1 ? "booking" : "bookings"}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-muted">
                  <Th>Customer</Th><Th>Call slot</Th><Th>Amount</Th><Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-muted"><Loader2 size={18} className="mr-2 inline animate-spin" />Loading bookings</td></tr>
                ) : filteredBookings.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-muted">No consultation bookings match these filters.</td></tr>
                ) : filteredBookings.map((item) => (
                  <tr
                    key={item.booking_id}
                    onClick={() => setSelectedId(item.booking_id)}
                    className={`cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50 ${selectedId === item.booking_id ? "bg-[#F5F9F9]" : ""}`}
                  >
                    <Td><div className="font-medium text-title">{item.name || "Unnamed customer"}</div><div className="text-xs text-muted">{item.email || "--"}</div></Td>
                    <Td><div className="font-medium text-title">{formatDateTime(item.slot_start)}</div><div className="text-xs text-muted">{formatDuration(item.slot_start, item.slot_end)}</div></Td>
                    <Td>{formatCurrency(item.amount, item.currency)}</Td>
                    <Td><StatusBadge status={item.status} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <BookingDetail booking={selected} />
      </div>
    </div>
  );
};

const BookingDetail = ({ booking }) => {
  if (!booking) return <aside className="rounded-lg bg-white p-6 text-center text-sm text-muted shadow stakeholder-summary-card">Select a booking to view details.</aside>;
  return (
    <aside className="h-fit rounded-lg bg-white p-5 shadow stakeholder-summary-card xl:sticky xl:top-5">
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-4">
        <div><div className="text-xs font-semibold uppercase tracking-wide text-muted">Booking detail</div><h2 className="mt-1 text-lg font-semibold text-title">{booking.name || "Unnamed customer"}</h2><p className="text-xs text-muted">{booking.booking_id}</p></div>
        <StatusBadge status={booking.status} />
      </div>
      <DetailSection icon={<UserRound size={16} />} title="Customer">
        <DetailLine label="Email" value={booking.email || "--"} />
        <DetailLine label="Phone" value={booking.phone ? <a href={`tel:${booking.phone}`} className="text-[#278b89] hover:underline">{booking.phone}</a> : "--"} />
      </DetailSection>
      <DetailSection icon={<CalendarCheck size={16} />} title="Call">
        <DetailLine label="Slot" value={formatDateTimeRange(booking.slot_start, booking.slot_end)} />
        <DetailLine label="Amount paid" value={formatCurrency(booking.amount, booking.currency)} />
        <DetailLine label="Booked on" value={formatDateTime(booking.created_at)} />
      </DetailSection>
      {booking.calendar_link && <a href={booking.calendar_link} target="_blank" rel="noreferrer" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#54B0AF] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#469d9c]"><ExternalLink size={16} />Open calendar event</a>}
      {booking.phone && <a href={`tel:${booking.phone}`} className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-200 px-4 py-2.5 text-sm font-semibold text-title hover:bg-gray-50"><Phone size={16} />Call customer</a>}
    </aside>
  );
};

const SummaryCard = ({ label, value, hint, tone = "default" }) => <div className={`rounded-lg border bg-white p-4 shadow-sm ${tone === "amber" ? "border-amber-200" : tone === "teal" ? "border-teal-100" : "border-gray-100"}`}><div className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</div><div className="mt-1 flex items-end justify-between"><span className="text-2xl font-semibold text-title">{value}</span>{hint && <span className="text-xs text-muted">{hint}</span>}</div></div>;
const DetailSection = ({ icon, title, children }) => <div className="border-b border-gray-100 py-4 last:border-0"><div className="mb-3 flex items-center gap-2 text-sm font-semibold text-title"><span className="text-[#54B0AF]">{icon}</span>{title}</div><div className="space-y-2">{children}</div></div>;
const DetailLine = ({ label, value }) => <div className="grid grid-cols-[96px_1fr] gap-3 text-sm"><span className="text-muted">{label}</span><span className="break-words text-right text-title">{value}</span></div>;
const Th = ({ children }) => <th className="px-4 py-3 font-semibold">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3 align-top">{children}</td>;

const StatusBadge = ({ status }) => {
  const style = status === "confirmed" ? "bg-emerald-50 text-emerald-700" : status === "paid" ? "bg-blue-50 text-blue-700" : status === "paid_needs_reschedule" || status === "payment_failed" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600";
  return <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${style}`}>{formatLabel(status) || "Unknown"}</span>;
};
const formatLabel = (value) => String(value || "").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const formatCurrency = (amount, currency = "INR") => amount == null ? "--" : new Intl.NumberFormat("en-IN", { style: "currency", currency: currency || "INR", maximumFractionDigits: 0 }).format(amount / 100);
const formatDateTime = (value) => value ? new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "--";
const formatDateTimeRange = (start, end) => start ? `${formatDateTime(start)}${end ? ` – ${new Intl.DateTimeFormat("en-IN", { timeStyle: "short" }).format(new Date(end))}` : ""}` : "--";
const formatDuration = (start, end) => start && end ? `${Math.max(0, Math.round((Date.parse(end) - Date.parse(start)) / 60000))} min call` : "";

export default ConsultationBookings;
